import cn from 'classnames';
import { useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { isOnPatientPage } from '@/shared/lib';
import { Avatar, Button, Modal } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { useLogout } from '@/features/logout';
import {
  OrganizationMFAEnrollment,
  useOrganizationMFASwitch,
} from '@/features/mfa';

import { i18n } from '../../config';
import { ClinicRow } from '../ClinicRow/ClinicRow';
import { DiagnocoinBalanceRow } from '../DiagnocoinBalanceRow/DiagnocoinBalanceRow';

import styles from './HeaderDrawer.module.scss';

export const HeaderDrawer = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { visible } = useAppSelector(modalModel.selectors.selectHeaderDrawer);
  const { userInitials, userFullName, userAvatarAssetSrc } = useAppSelector(
    userModel.selectors.selectUserAvatarData,
  );
  const { id, name, initials, logoSrc } = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationAvatarData,
  );
  const otherOrganizationsAvatarData = useAppSelector(
    organizationModel.selectors.selectOtherOrganizationsAvatarData,
  );
  const logout = useLogout();

  const handleClose = () => {
    dispatch(modalModel.actions.closeModal(ModalID.HeaderDrawer));
  };

  const navigateToCompanySettings = () => {
    navigate({
      pathname: PATHS.companySettings,
      search: 'tab=CompanyInfo',
    });
    handleClose();
  };

  const changeOrganization = (id: string) => {
    dispatch(organizationModel.thunks.setCurrentOrganizationByID(id));

    if (isOnPatientPage(location.pathname)) {
      navigate(PATHS.patients);
    }

    handleClose();
  };

  const {
    currentOrganizationName,
    pendingOrganization,
    requestSwitch,
    cancelSwitch,
    completeSwitch,
  } = useOrganizationMFASwitch(changeOrganization);

  return (
    <Modal
      data-component="header-drawer"
      title={formatMessage({
        id: 'headerDrawer.title',
        defaultMessage: 'Drawer',
      })}
      isOpen={visible}
      onCancel={handleClose}
      headerClassName={styles.header}
      bodyClassName={styles.body}
      headerContent={
        <>
          <Link
            className={cn(styles.myProfileLink, 'p2')}
            to={PATHS.personalSettings}
            onClick={handleClose}
          >
            <Avatar
              src={userAvatarAssetSrc}
              alt={userFullName}
              initials={userInitials}
              size={62}
            />
            <div className={styles.myProfileInfo}>
              <h3 className={cn(styles.textEllipsis, 'h3m')}>{userFullName}</h3>
              <p className={cn(styles.myProfile, 'p2')}>
                {formatMessage(i18n.myProfile)}
              </p>
            </div>
          </Link>
        </>
      }
      footer={
        <Button variant="secondary" onClick={logout}>
          {formatMessage({ id: 'global.signOut', defaultMessage: 'Sign out' })}
        </Button>
      }
    >
      <div className={styles.content}>
        {id && (
          <div className={styles.contentSection}>
            <h3 className={cn(styles.drawerTitle, 'h3m')}>
              {formatMessage(i18n.currentClinic)}
            </h3>
            <ClinicRow
              id={id}
              organizationName={name}
              initials={initials}
              avatarSrc={logoSrc}
              isCurrent
              onClick={navigateToCompanySettings}
            />

            <DiagnocoinBalanceRow className={styles.balance} />
          </div>
        )}
        {otherOrganizationsAvatarData.length > 0 && (
          <div className={styles.contentSection}>
            <h3 className={cn(styles.drawerTitle, 'h3m')}>
              {formatMessage(i18n.otherClinics)}
            </h3>
            {otherOrganizationsAvatarData.map((organization) => (
              <ClinicRow
                key={organization.id}
                id={organization.id}
                organizationName={organization.name}
                initials={organization.initials}
                avatarSrc={organization.logoSrc}
                isMFARequired={organization.isMFARequired}
                onClick={requestSwitch}
              />
            ))}
          </div>
        )}
      </div>

      {pendingOrganization && (
        <OrganizationMFAEnrollment
          targetOrganizationName={pendingOrganization.Name}
          currentOrganizationName={currentOrganizationName}
          onCancel={cancelSwitch}
          onSignInRequired={logout}
          onCompleted={completeSwitch}
        />
      )}
    </Modal>
  );
};
